import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { useCommentsContext } from '../../contexts/CommentsContext';
import { useCommentCreate, useCurrentUser } from '../../hooks';
import useCommentUpdate from '../../hooks/useCommentUpdate';
import { edit_profile_settings_path } from '../../routes';
import {
  Comment,
  CommentableType,
  NewComment,
  getCommentsKey,
} from '../../utils/backend-api/comments';
import { EnableProfileModal } from '../shared';
import MentionableInput from '../shared/MentionableInput';

const validationSchema = () =>
  Yup.object({
    body: Yup.string().required('Comment body is required'),
  });

// TODO: Disable commenting when replies_per_comment limit has been reached.
// See api/comments_controller#index
export default function CommentForm({
  comment,
  commentableId,
  commentableType,
  replyingTo,
  hideReplyForm,
  hideEditForm,
}: {
  comment?: Comment;
  commentableId: number;
  commentableType: CommentableType;
  replyingTo?: string;
  hideReplyForm?: () => void;
  hideEditForm?: () => void;
}) {
  const { currentUser } = useCurrentUser();
  const { mutateAsync: createAsync } = useCommentCreate();
  const { mutateAsync: updateAsync } = useCommentUpdate();
  const queryClient = useQueryClient();
  const { rootCommentableId, rootCommentableType } = useCommentsContext();
  const [canInitiallyComment] = useState(currentUser?.can_actually_comment);
  const isEditing = !!comment;

  const editBody = useMemo(() => {
    if (!comment?.body) return '';
    const body = ReactHtmlParser(comment.body);
    // Transform mentioned user links back to @user mention format
    return body
      .map((node) => {
        if (node.type === 'a' && node.props.className === 'mention') {
          const hrefParts = node.props.href.split('/');
          // The last part of the href is the username
          return `@${hrefParts[hrefParts.length - 1]}`;
        }
        return node;
      })
      .join('');
  }, [comment?.body]);

  const INITIAL_VALUES = {
    body: replyingTo ? `@${replyingTo} ` : editBody,
  };

  const handleSubmit = async ({ body }, { resetForm }) => {
    const data: NewComment = {
      body,
      commentable_id: commentableId,
      commentable_type: commentableType,
    };

    if (isEditing) {
      return updateAsync(
        { id: comment.id, body, commentable_id: commentableId, commentable_type: commentableType },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(getCommentsKey(rootCommentableId, rootCommentableType));
            toast.success('Your comment has been updated.');
            resetForm();
            hideEditForm();
          },
          onError: (e) => {
            console.error(e);
            toast.error('Comment was not updated. Please try again.');
          },
        }
      );
    }

    return createAsync(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(getCommentsKey(rootCommentableId, rootCommentableType));
        toast.success('Your comment has been created!');
        resetForm();
        if (hideReplyForm) hideReplyForm();
      },
      onError: (e) => {
        console.error(e);
        toast.error('Comment was not created. Please try again.');
      },
    });
  };

  const newCommentButtonText = replyingTo ? 'Reply' : 'Post new comment';
  const buttonText = isEditing ? 'Save' : newCommentButtonText;

  return (
    <Formik
      enableReinitialize
      initialValues={{ ...INITIAL_VALUES }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid, submitForm, values, handleChange }) => (
        <Form id="comment-form">
          <div className="comment-editor-container">
            <MentionableInput
              name="body"
              required={true}
              className="comment-editor"
              autoFocus={!!replyingTo}
              onChange={handleChange}
              value={values.body}
              contentId={commentableId}
              contentType={commentableType}
            />
          </div>
          <div className="actions">
            {currentUser?.can_actually_comment && (
              <button
                type="submit"
                className="button button-submit button-small"
                disabled={isSubmitting || !isValid}
              >
                {buttonText}
              </button>
            )}
            {!canInitiallyComment && (
              // Keep modal in DOM to avoid resetting it on re-render
              <div className={currentUser?.can_actually_comment ? 'hidden' : ''}>
                <EnableProfileModal
                  isLoggedIn={currentUser}
                  isSubscriber={!currentUser?.is_free_edition_user}
                  isTriggeredByComment={true}
                  commentButtonText={buttonText}
                  isCommentForForum={true}
                  hideReplyForm={hideReplyForm}
                  handleSubmitComment={submitForm}
                  rootCommentableId={rootCommentableId}
                  rootCommentableType={rootCommentableType}
                />
              </div>
            )}
            <div className="header-text-action">
              Want to edit your profile? <a href={edit_profile_settings_path()}>Edit Profile</a>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
